import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchDeconnexion } from "../connexion/connexionSlice";
import { API_URL } from "../../Constants";
import axios from "axios";
import Cookies from "js-cookie";

export const initialState = {
  loading: false,
  hasErrors: false,
  loaded: false,
  type: "",
  dashboard: [],
  live: {
    titres: [],
    designations: [],
    couverts: {
      enCours: [0, 0],
      realise: [0, 0],
    },
    ca: {
      enCours: [0, 0],
      realise: [0, 0],
    },
    totals: {
      CVTotal: 0,
      TotalCA: 0,
    },
    dates: {
      serviceEnCours: null,
      finServiceEnCours: null,
      heureFinServiceEnCours: null,
    },
    etatDuService: null,
  },
  // Nouveaux états pour le suivi de l'enregistrement de l'alias
  aliasStatus: "idle", // "idle" | "loading" | "succeeded" | "failed"
  aliasError: null,
};

// Thunk pour enregistrer (ou mettre à jour) l'alias via le back  
export const registerAlias = createAsyncThunk(
  "dashboard/registerAlias",
  async (alias, { rejectWithValue }) => {
    try {
      // Récupération du token CSRF depuis le cookie (le nom par défaut est "csrf_access_token" avec flask-jwt-extended)
      const csrfToken = Cookies.get("csrf_access_token");
      const config = {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": csrfToken,
        },
        withCredentials: true,
      };
      // Assurez-vous que l'URL correspond à la route enregistrée dans votre back,
      // ici par exemple : `${API_URL}/compte/enregistrement/alias`
      const response = await axios.post(`${API_URL}/compte/enregistrement/alias`, { alias }, config);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data
          ? error.response.data
          : { message: "Erreur réseau" }
      );
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    getDashboard: (state) => {
      state.loading = true;
      state.loaded = false;
    },
    getDashboardSuccess: (state, { payload }) => {
      state.dashboard = payload;
      state.loading = false;
      state.hasErrors = false;
      state.loaded = true;
    },
    getLiveSuccess: (state, { payload }) => {
      state.live = payload; // Mise à jour des données live
      state.loading = false;
      state.hasErrors = false;
      state.loaded = true;
    },
    getDashboardFailure: (state) => {
      state.loading = false;
      state.hasErrors = true;
      state.loaded = false;
    },
    setType: (state, { payload }) => {
      state.type = payload;
    },
    // Optionnel : on peut réinitialiser le status alias
    resetAliasStatus: (state) => {
      state.aliasStatus = "idle";
      state.aliasError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerAlias.pending, (state) => {
        state.aliasStatus = "loading";
        state.aliasError = null;
      })
      .addCase(registerAlias.fulfilled, (state, action) => {
        state.aliasStatus = "succeeded";
        // On pourrait éventuellement stocker ici l'alias dans l'état si nécessaire
      })
      .addCase(registerAlias.rejected, (state, action) => {
        state.aliasStatus = "failed";
        state.aliasError = action.payload?.message || "Erreur lors de l'enregistrement de l'alias.";
      });
  },
});

export const {
  getDashboard,
  getDashboardSuccess,
  getLiveSuccess,
  getDashboardFailure,
  setType,
  resetAliasStatus,
} = dashboardSlice.actions;

export const dashboardSelector = (state) => state.dashboard;
export const liveSelector = (state) => state.dashboard.live; // Sélecteur pour les données live

export default dashboardSlice.reducer;

// Fonction pour l'API principale (fetchDashboard)
// Modification : utilisation de getState().connexion.utilisateur.rest_id_affichage
// pour obtenir la valeur mise à jour du restaurant.
export function fetchDashboard(type, date_min = 0, date_max = 0) {
  return async (dispatch, getState) => {
    // On utilise la valeur à jour dans l'objet utilisateur
    const restaurantToUse = getState().connexion.utilisateur.rest_id_affichage;
    dispatch(getDashboard());
    dispatch(setType(type));
    try {
      const config = {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      };
      const response = await axios.get(
        `${API_URL}/web/dashboard/${type}/${date_min}/${date_max}/${restaurantToUse}`,
        config
      );
      dispatch(getDashboardSuccess(response.data));
      return response.data; // Retourne les données
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch(fetchDeconnexion());
      }
      dispatch(getDashboardFailure());
    }
  };
}

const defaultValue = (value, defaultVal = 0) => {
  return value !== undefined && value !== null ? value : defaultVal;
};

// Fonction pour l'API live (fetchLive)
export function fetchLive(type, date_min = 0, date_max = 0) {
  return async (dispatch, getState) => {
    // Ici aussi, on utilise la nouvelle valeur
    const restaurantToUse = getState().connexion.utilisateur.rest_id_affichage;
    dispatch(getDashboard());
    dispatch(setType(type));
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        withCredentials: true,
      };
      const response = await axios.get(
        `${API_URL}/web/dashboard/live/${type}/${date_min}/${date_max}/${restaurantToUse}?t=${new Date().getTime()}`,
        config
      );
      const data = response.data;
      // Transformation des données
      const titres = data[0]?.ligne_donnee || [];
      const designations = data[1]?.ligne_donnee || [];

      const couvertsBarEnCours = defaultValue(data[2]?.NbTicketBar);
      const couvertsRestoEnCours = defaultValue(data[2]?.NbCouvert);
      const couvertsBarRealise = defaultValue(data[3]?.NbTicketBar);
      const couvertsRestoRealise = defaultValue(data[3]?.NbCouvert);

      const caBrasserieEnCours = defaultValue(data[2]?.["CA.Brasserie"]);
      const caRestaurantEnCours = defaultValue(data[2]?.["CA.Restaurant"]);
      const caBrasserieRealise = defaultValue(data[3]?.["CA.Brasserie"]);
      const caRestaurantRealise = defaultValue(data[3]?.["CA.Restaurant"]);

      const CAenCours = defaultValue(data[2]?.["CA.Brasserie"]) + defaultValue(data[2]?.["CA.Restaurant"]);
      const CArealise = defaultValue(data[3]?.["CA.Brasserie"]) + defaultValue(data[3]?.["CA.Restaurant"]);

      const CVenCours = defaultValue(data[2]?.["NbCouvert"]) + defaultValue(data[2]?.["NbTicketBar"]);
      const CVrealise = defaultValue(data[3]?.["NbCouvert"]) + defaultValue(data[3]?.["NbTicketBar"]);

      const CVTotal = CVenCours + CVrealise;
      const TotalCA = CAenCours + CArealise;

      const dateServiceEnCours = data[2]?.DateService || null;
      const dateFinServiceEnCours = data[2]?.DateFinService || null;
      const heureFinServiceEnCours = data[2]?.HeureFinService || null;

      const etatDuService =
        data[2]?.["impr_edit"] !== undefined && data[2]?.["impr_edit"] !== null
          ? data[2]["impr_edit"]
          : defaultValue(data[3]?.["impr_edit"], null);

      const liveData = {
        titres,
        designations,
        couverts: {
          enCours: [couvertsBarEnCours, couvertsRestoEnCours],
          realise: [couvertsBarRealise, couvertsRestoRealise],
        },
        ca: {
          enCours: [caBrasserieEnCours, caRestaurantEnCours],
          realise: [caBrasserieRealise, caRestaurantRealise],
        },
        totals: {
          CVTotal,
          TotalCA,
        },
        dates: {
          serviceEnCours: dateServiceEnCours,
          finServiceEnCours: dateFinServiceEnCours,
          heureFinServiceEnCours: heureFinServiceEnCours,
        },
        etatDuService,
      };

      dispatch(getLiveSuccess(liveData));
      return liveData; // Retourne les données
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch(fetchDeconnexion());
      }
      dispatch(getDashboardFailure());
    }
  };
}
